import { useEffect, useRef, useState } from 'react';
import { INSTAGRAM, REELS } from '../../data/reels';
import './Reels.css';

const SHORTCODE = /\/(p|reel|tv)\/([^/?#]+)/;

const ITEMS = REELS.reduce((all, link) => {
  const found = SHORTCODE.exec(link);
  if (!found) return all;
  const kind = found[1] === 'tv' ? 'p' : found[1];
  const code = found[2];
  all.push({
    code,
    video: `/api/reels/${code}/video`,
    poster: `/api/reels/${code}/poster`,
    modal: `https://www.instagram.com/${kind}/${code}/embed/captioned/`,
  });
  return all;
}, []);

function Arrow({ direction, onClick }) {
  return (
    <button
      type="button"
      className={`reels__arrow reels__arrow--${direction === -1 ? 'prev' : 'next'}`}
      onClick={onClick}
      aria-label={direction === -1 ? 'Previous' : 'Next'}
    >
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path
          d={direction === -1 ? 'M14.5 5 8 12l6.5 7' : 'M9.5 5 16 12l-6.5 7'}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}

export default function Reels() {
  const strip = useRef(null);
  const [open, setOpen] = useState(null);
  const [missing, setMissing] = useState({});

  useEffect(() => {
    if (!open) return undefined;
    function onKey(event) {
      if (event.key === 'Escape') setOpen(null);
    }
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open]);

  if (ITEMS.length === 0) return null;

  function nudge(direction) {
    const node = strip.current;
    if (!node) return;
    const tile = node.firstElementChild;
    const step = tile ? tile.getBoundingClientRect().width + 8 : node.clientWidth * 0.8;
    node.scrollBy({ left: direction * step, behavior: 'smooth' });
  }

  return (
    <section className="section reels" id="reels">
      <div className="reels__bar">
        <a className="reels__handle" href={INSTAGRAM.url} target="_blank" rel="noreferrer">
          @{INSTAGRAM.handle}
        </a>
        <a className="reels__follow" href={INSTAGRAM.url} target="_blank" rel="noreferrer">
          Follow
        </a>
      </div>

      <div className="reels__strip" ref={strip}>
        {ITEMS.map((item) => (
          <button type="button" className="reel" key={item.code} onClick={() => setOpen(item)}>
            {missing[item.code] ? (
              <span className="reel__badge" aria-hidden="true">
                <svg viewBox="0 0 24 24">
                  <path d="M8 5.5h8M4.5 8.5h15M6 8.5h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2z" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M11 12.2v4.6l4-2.3z" fill="currentColor" />
                </svg>
              </span>
            ) : (
              <video
                className="reel__video"
                src={item.video}
                poster={item.poster}
                autoPlay
                loop
                muted
                playsInline
                preload="metadata"
                ref={(node) => {
                  if (node) node.muted = true;
                }}
                onError={() => setMissing((prev) => ({ ...prev, [item.code]: true }))}
              />
            )}
          </button>
        ))}
      </div>

      <div className="reels__nav">
        <Arrow direction={-1} onClick={() => nudge(-1)} />
        <Arrow direction={1} onClick={() => nudge(1)} />
      </div>

      {open && (
        <div className="reel-modal" onClick={() => setOpen(null)}>
          <div className="reel-modal__box" onClick={(event) => event.stopPropagation()}>
            <iframe
              className="reel-modal__frame"
              src={open.modal}
              title="Instagram post"
              scrolling="no"
              allow="autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
            />
            <button type="button" className="reel-modal__close" onClick={() => setOpen(null)} aria-label="Close" autoFocus>
              ×
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
